/**
 * JobMockAI — Navigation configuration
 *
 * Central definition of all sidebar navigation items, grouped by section.
 * Used by the AppShell sidebar and breadcrumb components.
 */
import {
  Dashboard as DashboardIcon,
  Home,
  TrendingUp,
  Work,
  WorkOutline,
  Mic,
  History,
  Replay,
  People,
  PersonSearch,
  Videocam,
  CalendarMonth,
  School,
  SmartToy,
  SupportAgent,
  EmojiEvents,
  MilitaryTech,
  BusinessCenter,
  ListAlt,
  Storefront,
  EventNote,
  Person,
  Settings,
  CreditCard,
  CardGiftcard,
  GroupAdd,
  Notifications,
  Visibility,
  AdminPanelSettings,
  AssignmentInd,
  Receipt,
  SmartToy as SmartToyIcon,
  Business,
  Description,
  Analytics,
  Feedback as FeedbackIcon,
  RateReview,
  DynamicFeed,
  Chat as ChatIcon,
  TravelExplore,
  RecordVoiceOver,
  VideoLibrary,
  MovieCreation,
  Tune,
  Extension,
  Quiz,
  MenuBook,
  AutoStories,
  Handshake,
  Forum,
  AccountCircle,
  Build,
  SportsEsports,
  LocalLibrary,
} from '@mui/icons-material';

/**
 * Each group has:
 *   label       — section header
 *   sectionIcon — MUI icon for the section header (sidebar + nav preferences)
 *   items       — array of { label, path, icon, featureFlag?, adminOnly?, badge? }
 */
const navigationConfig = [
  {
    label: 'Dashboard',
    sectionIcon: DashboardIcon,
    items: [
      { label: 'Overview', path: '/', icon: DashboardIcon },
      { label: 'Progress & Badges', path: '/progress', icon: TrendingUp },
      { label: 'Career Growth', path: '/career-plan', icon: WorkOutline },
    ],
  },
  {
    label: 'Practice',
    sectionIcon: Mic,
    items: [
      { label: 'Start Interview', path: '/interview/start', icon: Mic },
      { label: 'Custom Interview', path: '/interview/custom-builder', icon: Build },
      { label: 'Voice Coach', path: '/voice-coach', icon: RecordVoiceOver },
      { label: 'Practice Interviews', path: '/practice/interviews', icon: Storefront, featureFlag: 'practice_interview_marketplace' },
      { label: 'Interview History', path: '/interview/history', icon: History },
      { label: 'Local Recordings', path: '/dashboard/recordings', icon: VideoLibrary, featureFlag: 'local_recording' },
      { label: 'Interview Replay', path: '/replay', icon: Replay },
      { label: 'AI Observer Reports', path: '/ai-observations', icon: SmartToy },
    ],
  },
  {
    label: 'Campus',
    sectionIcon: School,
    /** When campus_placement is on, Sidebar elevates this group (order + visual emphasis). */
    elevateOnFlag: 'campus_placement',
    items: [
      { label: 'Placement Prep Hub', path: '/campus/placement', icon: School, featureFlag: 'campus_placement' },
      { label: 'TPO Cohort', path: '/campus/tpo', icon: GroupAdd, featureFlag: 'campus_placement' },
    ],
  },
  {
    label: 'Practice with Peers',
    sectionIcon: Handshake,
    items: [
      { label: 'Interview Rooms',  path: '/rooms',                   icon: Videocam },
      { label: 'Schedules',        path: '/schedules',               icon: CalendarMonth },
      { label: 'Find Partners',    path: '/community/discover',      icon: PersonSearch, featureFlag: 'community' },
      { label: 'My Connections',   path: '/community/connections',   icon: People,       featureFlag: 'community' },
      { label: 'Community Feed',   path: '/community/feed',          icon: DynamicFeed,  featureFlag: 'community' },
    ],
  },
  {
    label: 'Learn',
    sectionIcon: AutoStories,
    items: [
      { label: 'Learning Hub', path: '/learning-hub', icon: MenuBook },
      { label: 'AI Career Coach', path: '/coach', icon: SmartToy },
      { label: 'Interview Agents', path: '/agents', icon: SupportAgent, featureFlag: 'job_hunt_agent' },
    ],
  },
  {
    label: 'Compete',
    sectionIcon: EmojiEvents,
    items: [
      { label: 'Leaderboard', path: '/leaderboard', icon: EmojiEvents },
      { label: 'Achievements', path: '/achievements', icon: MilitaryTech },
    ],
  },
  {
    label: 'Jobs',
    sectionIcon: Work,
    items: [
      { label: 'Browse Jobs', path: '/browse-jobs', icon: TravelExplore },
      { label: 'Job Board', path: '/job-board', icon: BusinessCenter },
      { label: 'Hiring Marketplace', path: '/hiring', icon: Business },
      { label: 'My Applications', path: '/my-applications', icon: ListAlt },
      { label: 'Job Visibility', path: '/hiring/settings', icon: Visibility },
    ],
  },
  {
    label: 'Resume',
    sectionIcon: Description,
    items: [
      { label: 'My Resumes', path: '/resumes', icon: Description },
      { label: 'AI Analyzer', path: '/resumes/analyzer', icon: Analytics },
    ],
  },
  {
    label: 'Marketplace',
    sectionIcon: Storefront,
    /** When interviewer_marketplace is on, Sidebar elevates this group near Practice/Campus. */
    elevateOnFlag: 'interviewer_marketplace',
    items: [
      { label: 'Become an Interviewer', path: '/marketplace/apply', icon: WorkOutline, becomeInterviewerOnly: true },
      { label: 'Browse Interviewers', path: '/marketplace', icon: Storefront },
      { label: 'My Bookings', path: '/marketplace/bookings', icon: EventNote },
      { label: 'Interviewer Dashboard', path: '/interviewer/dashboard', icon: Videocam, interviewerOnly: true },
    ],
  },
  {
    label: 'Feedback & Reviews',
    sectionIcon: Forum,
    items: [
      { label: 'Give Feedback', path: '/feedback', icon: FeedbackIcon },
      { label: 'Reviews', path: '/reviews', icon: RateReview },
    ],
  },
  {
    label: 'Account',
    sectionIcon: AccountCircle,
    items: [
      { label: 'Profile & Settings', path: '/profile', icon: Person },
      { label: 'Billing', path: '/billing', icon: CreditCard },
      { label: 'Referrals & Rewards', path: '/referrals', icon: GroupAdd },
      { label: 'Rewards Store', path: '/rewards', icon: CardGiftcard },
      { label: 'Notifications', path: '/notifications', icon: Notifications },
    ],
  },
];

/**
 * Daily brain warm-up games — gated by daily_puzzle feature flag.
 */
export const gamesNavigationConfig = {
  label: 'Games',
  sectionIcon: SportsEsports,
  items: [
    { label: 'Games', path: '/games', icon: Extension, featureFlag: 'daily_puzzle' },
  ],
};

/**
 * Demo Video Studio — visible to admin, super_admin, and marketing roles.
 */
export const demoStudioNavigationConfig = {
  label: 'Studio',
  sectionIcon: MovieCreation,
  items: [
    { label: 'Demo Video Studio', path: '/studio/demo', icon: MovieCreation, demoStudioOnly: true },
  ],
};

/**
 * Admin-only group — only rendered when user.role is ADMIN or SUPER_ADMIN.
 */
export const adminNavigationConfig = {
  label: 'Admin',
  sectionIcon: Build,
  items: [
    { label: 'Admin Dashboard', path: '/admin', icon: AdminPanelSettings, adminOnly: true },
    { label: 'User Management', path: '/admin/users', icon: People, adminOnly: true },
    { label: 'Feature Flags', path: '/admin/feature-flags', icon: Settings, adminOnly: true },
    { label: 'Plan Quotas', path: '/admin/plans', icon: Tune, adminOnly: true },
    { label: 'Audit Logs', path: '/admin/audit-logs', icon: Receipt, adminOnly: true },
    { label: 'AI Usage', path: '/admin/ai-usage', icon: SmartToyIcon, adminOnly: true },
    { label: 'Companies', path: '/admin/companies', icon: Business, adminOnly: true },
    { label: 'Interviewer Requests', path: '/admin/interviewer-applications', icon: AssignmentInd, adminOnly: true },
    { label: 'Founding Interviewers', path: '/admin/founding-interviewer-applications', icon: RecordVoiceOver, adminOnly: true },
    { label: 'Interviewers', path: '/admin/interviewers', icon: Videocam, adminOnly: true },
    { label: 'Practice Interview Moderation', path: '/admin/practice-interview-moderation', icon: Quiz, adminOnly: true },
    { label: 'Video upload limits', path: '/admin/video-limits', icon: Videocam, adminOnly: true },
    { label: 'Study Catalog', path: '/admin/study-catalog', icon: LocalLibrary, adminOnly: true },
  ],
};

/**
 * LinkedIn-style AppShell top bar primary destinations (icon + short label).
 * Full catalog remains in the left sidebar (with progressive defaults).
 */
export const topNavItems = [
  { label: 'Home', path: '/', icon: Home },
  { label: 'Practice', path: '/interview/start', icon: Mic },
  { label: 'Experts', path: '/marketplace', icon: Storefront, featureFlag: 'interviewer_marketplace' },
  { label: 'Jobs', path: '/browse-jobs', icon: Work },
  { label: 'Network', path: '/community/discover', icon: People, featureFlag: 'community' },
  { label: 'Messaging', path: '/community/messages', icon: ChatIcon, featureFlag: 'community' },
];

export default navigationConfig;
